<script setup>
// Cobrança via Pix (interactiveMessage/nativeFlow payment do WhatsApp),
// encaminhada pelo ByCall como texto:
//   💠 *Cobrança via Pix*
//   <merchant/nota>              (opcional)
//   🔑 Chave (<tipo>): <chave>
//   💰 BRL 49.90                 (opcional)
//   _Ref: <id>_                  (opcional)
// Card no layout do WhatsApp: ícone Pix + título/chave, divisória, botão
// "Copiar chave Pix" full-width. Detecção/roteamento no Message.vue (marcador 💠).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import BaseBubble from 'next/message/bubbles/Base.vue';

const { t } = useI18n();
const { content } = useMessageContext();

const copied = ref(false);

// rótulo do tipo de chave no estilo WhatsApp
const KEY_LABELS = {
  telefone: 'Celular',
  email: 'E-mail',
  cpf: 'CPF',
  cnpj: 'CNPJ',
  'chave aleatória': 'Chave aleatória',
};

const formatKey = (key, keyType) => {
  if (keyType !== 'telefone') return key;
  const digits = key.replace(/\D/g, '');
  const d = digits.startsWith('55') ? digits.slice(2) : digits;
  if (d.length === 11) return `${d.slice(0, 2)} ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 2)} ${d.slice(2, 6)}-${d.slice(6)}`;
  return key;
};

const pix = computed(() => {
  const raw = String(content?.value || '');
  const lines = raw.split('\n');

  const grab = re => (raw.match(re) || [])[1]?.trim() || '';
  const titleRaw = grab(/💠\s*\*([^*]+)\*/) || 'Cobrança via Pix';

  const keyLine = raw.match(/🔑\s*Chave\s*\(([^)]*)\):\s*(.+)/);
  const keyType = keyLine?.[1]?.trim() || '';
  const key = keyLine?.[2]?.trim() || '';

  const amount = grab(/💰\s*(.+)/);
  const refId = grab(/_Ref:\s*([^_]+)_/);

  // merchant/nota: linhas entre o título (💠) e a chave (🔑)
  const startIdx = lines.findIndex(l => l.includes('💠'));
  const keyIdx = lines.findIndex(l => l.includes('🔑'));
  let merchant = '';
  if (startIdx > -1 && keyIdx > startIdx) {
    merchant = lines
      .slice(startIdx + 1, keyIdx)
      .map(l => l.trim())
      .filter(Boolean)
      .join(' ');
  }

  const keyLabel = KEY_LABELS[keyType.toLowerCase()] || keyType || 'Chave';
  return {
    title: merchant || titleRaw,
    keyLabel,
    key,
    keyDisplay: formatKey(key, keyType.toLowerCase()),
    amount,
    ref: refId,
  };
});

const copyKey = async () => {
  try {
    await navigator.clipboard.writeText(pix.value.key);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (e) {
    copied.value = false;
  }
};
</script>

<template>
  <BaseBubble
    class="!p-0 overflow-hidden !max-w-[320px] min-w-[268px]"
    data-bubble-name="pix"
    hide-meta
  >
    <div class="flex items-center gap-3 p-3">
      <div
        class="flex items-center justify-center rounded-full size-11 shrink-0 bg-n-teal-4"
      >
        <span class="i-ph-diamonds-four-fill size-6 text-n-teal-11" />
      </div>
      <div class="flex flex-col min-w-0 gap-0.5">
        <span class="text-sm font-semibold leading-tight text-n-slate-12">
          {{ pix.title }}
        </span>
        <span class="text-xs leading-tight truncate text-n-slate-11">
          {{ pix.keyLabel }}: {{ pix.keyDisplay }}
        </span>
        <span
          v-if="pix.amount"
          class="mt-0.5 text-base font-bold leading-tight text-n-slate-12"
        >
          {{ pix.amount }}
        </span>
        <span v-if="pix.ref" class="text-[0.625rem] text-n-slate-10">
          {{ t('CONVERSATION.PIX_BUBBLE.REF', { ref: pix.ref }) }}
        </span>
      </div>
    </div>

    <button
      type="button"
      class="flex items-center justify-center w-full gap-2 py-2.5 text-sm font-medium border-t border-n-weak text-n-teal-11 hover:bg-n-alpha-1"
      @click="copyKey"
    >
      <span :class="copied ? 'i-ph-check-bold' : 'i-ph-copy'" class="size-4" />
      {{ copied ? 'Chave copiada' : 'Copiar chave Pix' }}
    </button>
  </BaseBubble>
</template>
